import { LoaderCircleIcon } from 'lucide-react'
import { MotionConfig } from 'motion/react'
import { Navigate, Outlet, useLocation } from 'react-router'

import Toolbar from 'apps/components/toolbar/Toolbar'
import { Banner } from 'slab'

import { RedirectToEmployerHomeButton } from './RedirectToEmployerHomeButton'
import { EmployerProvider } from '../employer_onboarding/EmployerProvider'
import { type FeatureFlags, FlagProvider } from '../employer_onboarding/FlagProvider'

interface OwnerInvitationFormLayoutProps {
  employerId: string | null
  authenticated?: boolean
  featureFlags?: FeatureFlags
  ownershipInvitationManagedByThatch?: boolean
}

function NavigatePreserveSearch ({ to, replace }: { to: string; replace?: boolean }) {
  const location = useLocation()
  return <Navigate to={{ pathname: to, search: location.search }} replace={replace} />
}

const OWNER_INVITATION_ALREADY_COMPLETED_PATH = '/employers/verification/owner_invitation/already-completed'

export function OwnerInvitationFormLayout ({
  employerId,
  featureFlags = {},
  ownershipInvitationManagedByThatch = false
}: OwnerInvitationFormLayoutProps) {
  const location = useLocation()

  function renderContent ({
    loading,
    error,
    employer,
  }: {
    loading: boolean;
    error: Error | null;
    employer: any;
  }) {
    if (loading) {
      return (
        <div className='flex grow items-center justify-center'>
          <LoaderCircleIcon size={24} className='animate-spin text-gray-500' />
        </div>
      )
    }

    if (error) {
      return (
        <div className='mt-20 w-full max-w-[45rem] mx-auto'>
          <Banner type='critical' title='Error loading employer'>
            {error.message}
          </Banner>
        </div>
      )
    }

    // Skip redirect logic if we're already on the already-completed page
    const isAlreadyCompletedPage = location.pathname === OWNER_INVITATION_ALREADY_COMPLETED_PATH

    if (!isAlreadyCompletedPage) {
      // Handle redirects at the layout level
      // If the owner invitation form is already completed (submitted), redirect to already-completed page
      if (employer?.owner_invitation_form_status === 'submitted') {
        return <NavigatePreserveSearch to={OWNER_INVITATION_ALREADY_COMPLETED_PATH} replace />
      }

      if (ownershipInvitationManagedByThatch) {
        return <NavigatePreserveSearch to='/employers/verification/owner_invitation' replace />
      }
    }

    return <Outlet />
  }

  return (
    <FlagProvider flags={featureFlags}>
      <EmployerProvider employerId={employerId}>
        {({ loading, error, employer }) => (
          <div className='min-h-svh flex flex-col items-stretch isolate bg-gradient-to-t from-thatch-100 to-gray-50 bg-fixed'>
            <Toolbar trailingComponent={<RedirectToEmployerHomeButton />} />
            <div className='flex flex-col grow px-4 xs:px-6 pt-6 xs:pt-10 pb-4 xs:pb-6'>
              <MotionConfig transition={{ duration: 0.3 }}>
                {renderContent({ loading, error, employer })}
              </MotionConfig>
            </div>
          </div>
        )}
      </EmployerProvider>
    </FlagProvider>
  )
}
